import { MouseEvent, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import { getInventoryHost } from 'config/host'
import { FLAG_LABEL, MovementRow } from '../types'
import Mark from './Mark'

/**
 * Open-in-new-window + copy-link affordances for a linked counterparty, mirroring the
 * design reference. Both act on the absolute URL — internal routes (transfer orders) are
 * resolved against the inventory host so the copied link works outside the app.
 */
function LinkActions( { url }: { url: string } ) {
	const [ copied, setCopied ] = useState( false )

	const openInNewWindow = ( event: MouseEvent ) => {
		event.stopPropagation()
		window.open( url, '_blank', 'noopener,noreferrer' )
	}

	const copyLink = ( event: MouseEvent ) => {
		event.stopPropagation()
		if ( !navigator.clipboard ) {
			return
		}
		navigator.clipboard.writeText( url )
		setCopied( true )
		setTimeout( () => setCopied( false ), 1200 )
	}

	return (
		<>
			<Tooltip title="Open in new window">
				<IconButton size="small" onClick={openInNewWindow} aria-label="Open in new window" sx={{ color: 'text.secondary' }}>
					<OpenInNewIcon sx={{ fontSize: 16 }} />
				</IconButton>
			</Tooltip>
			<Tooltip title={copied ? 'Copied' : 'Copy link'}>
				<IconButton size="small" onClick={copyLink} aria-label="Copy link" sx={{ color: copied ? 'success.main' : 'text.secondary' }}>
					{copied ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
				</IconButton>
			</Tooltip>
		</>
	)
}

/**
 * The non-inventory side of a movement — a purchase order, transfer order, work order,
 * etc. Links internally (transfer orders) or externally (CostCapture purchase orders)
 * where a destination exists, with open-in-new-window and copy-link actions; degrades to
 * a labelled reference with an "unlinked" mark when it cannot be resolved, and shows an
 * explicit marker when no reference was ever recorded.
 */
export default function CounterpartyCell( { row }: { row: MovementRow } ) {
	const { counterparty, flags } = row

	if ( counterparty.missing ) {
		return <Mark title={FLAG_LABEL.no_counterparty_recorded}>No reference recorded</Mark>
	}

	const unlinkedFlag = flags.includes( 'unresolved_counterparty' )
		? 'unresolved_counterparty'
		: flags.includes( 'id_only' ) ? 'id_only' : null

	// Absolute URL for the counterparty's destination: external links are already absolute;
	// internal routes are resolved against the inventory host so the copied link stands alone.
	const url = counterparty.href ?? ( counterparty.to ? `${getInventoryHost()}${counterparty.to}` : null )

	const label = counterparty.to ? (
		<Link component={RouterLink} to={counterparty.to} underline="hover" onClick={( event ) => event.stopPropagation()}>{counterparty.display}</Link>
	) : counterparty.href ? (
		<Link href={counterparty.href} target="_blank" rel="noopener" underline="hover" onClick={( event ) => event.stopPropagation()}>{counterparty.display}</Link>
	) : (
		<Typography component="span" variant="body2" color="text.secondary">{counterparty.display}</Typography>
	)

	return (
		<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, flexWrap: 'wrap' }}>
			{label}
			{url && <LinkActions url={url} />}
			{unlinkedFlag && <Mark title={FLAG_LABEL[ unlinkedFlag ]}>unlinked</Mark>}
		</Box>
	)
}
